import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../../services/api';
import { getStoredSessionToken } from '../../../services/sessionTokens';

const RETRY_MESSAGE = 'Could not check the latest payment rules. Retry before paying.';
const UNAVAILABLE_MESSAGE = 'This payment method is no longer available. Choose another method.';
const rulesError = message => Object.assign(new Error(message), { paymentRulesError: true });

// Never refresh payment rules from the public, unscoped portal catalog. The
// authenticated player catalog includes this tenant's rows and access controls.
export default function useLatestDepositMethod({ enabled, methodId, playerId, tenantId, step }) {
  const key = `${playerId || ''}:${tenantId || ''}:${methodId || ''}`;
  const currentKey = useRef(key);
  currentKey.current = key;
  const active = useRef(enabled);
  active.current = enabled;
  const generation = useRef(0);
  const invalidate = useCallback(() => { ++generation.current; }, []);
  const [state, setState] = useState({ key: '', status: 'idle', method: null, error: '' });

  const refresh = useCallback(async () => {
    if (!enabled || !methodId) throw rulesError(RETRY_MESSAGE);
    const requestGeneration = ++generation.current;
    const sessionToken = getStoredSessionToken();
    setState(previous => ({
      key, status: 'loading', method: previous.key === key ? previous.method : null, error: '',
    }));
    try {
      const response = await api.get('/api/payment-methods', {
        params: { flow: 'deposit' }, timeout: 10000, expectedSessionToken: sessionToken,
      });
      if (!active.current || generation.current !== requestGeneration || currentKey.current !== key
          || getStoredSessionToken() !== sessionToken) throw new Error(RETRY_MESSAGE);
      const list = response.data?.methods ?? response.data?.payment_methods;
      if (!Array.isArray(list)) throw new Error(RETRY_MESSAGE);
      const fresh = list.find(item => String(item?.id) === String(methodId));
      if (!fresh || fresh.enabled === false || fresh.player_enabled === false
          || !['deposit', 'both'].includes(fresh.flow)) {
        throw new Error(UNAVAILABLE_MESSAGE);
      }
      // Missing/invalid constraints are not permission to reuse old limits.
      if (!['number', 'string'].includes(typeof fresh.min_amount) || String(fresh.min_amount).trim() === ''
          || !Number.isFinite(Number(fresh.min_amount))
          || Number(fresh.min_amount) < 0) throw new Error(RETRY_MESSAGE);
      setState({ key, status: 'ready', method: fresh, error: '' });
      return { method: fresh, sessionToken };
    } catch (error) {
      const message = error?.message === UNAVAILABLE_MESSAGE ? UNAVAILABLE_MESSAGE : RETRY_MESSAGE;
      if (active.current && generation.current === requestGeneration && currentKey.current === key) {
        setState({ key, status: 'error', method: null, error: message });
      }
      throw rulesError(message);
    }
  }, [enabled, key, methodId]);

  useEffect(() => {
    if (!enabled || !methodId) {
      invalidate();
      setState({ key: '', status: 'idle', method: null, error: '' });
      return undefined;
    }
    const reload = () => { refresh().catch(() => {}); };
    const onVisible = () => { if (document.visibilityState === 'visible') reload(); };
    reload();
    window.addEventListener('focus', reload);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      invalidate();
      window.removeEventListener('focus', reload);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, invalidate, methodId, refresh, step]);

  return {
    method: enabled && state.key === key ? state.method : null,
    status: !enabled ? 'idle' : state.key === key ? state.status : 'loading',
    error: enabled && state.key === key ? state.error : '',
    refresh,
  };
}
